import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Mic, Cpu, Calendar } from 'lucide-react';
import useIsMobile from '../hooks/useIsMobile';

const steps = [
  {
    number: '01', icon: Mic, title: 'Empty Your Mind',
    description: "Stop hoarding thoughts in your head. Voice it, type it, paste a link — whatever it is, let it out. Neuron is listening.",
    color: '#8B5CF6', glowColor: 'rgba(139,92,246,0.4)',
    bgFrom: 'rgba(139,92,246,0.08)', bgTo: 'rgba(139,92,246,0.02)',
    tags: ['Instant Relief', 'Unfiltered', 'Speak Freely'],
  },
  {
    number: '02', icon: Cpu, title: 'Let Go of the Details',
    description: "You don't need to categorize or organize ever again. Neuron quietly untangles your chaos — knowing what belongs in the past and what needs action in the future.",
    color: '#6366F1', glowColor: 'rgba(99,102,241,0.4)',
    bgFrom: 'rgba(99,102,241,0.08)', bgTo: 'rgba(99,102,241,0.02)',
    tags: ['Zero Organizing', 'Context Aware', 'Silent Sorting'],
  },
  {
    number: '03', icon: Calendar, title: 'Nothing Falls Through',
    description: "Never feel that sinking 'what did I forget?' panic again. Your deadlines are handled. Simply ask — it's already waiting.",
    color: '#10B981', glowColor: 'rgba(16,185,129,0.4)',
    bgFrom: 'rgba(16,185,129,0.08)', bgTo: 'rgba(16,185,129,0.02)',
    tags: ['Perfect Recall', 'Mental Clarity', 'Absolute Certainty'],
  },
];

const StepCard = ({ step, index }) => {
  const IconComponent = step.icon;
  return (
    <motion.div
      className="relative group"
      initial={{ opacity: 0, y: 36 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-5%' }}
      transition={{ duration: 0.65, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
    >
      <div className="absolute -inset-1 rounded-2xl opacity-0 group-hover:opacity-100 transition-all duration-500 blur-xl"
        style={{ background: `radial-gradient(circle, ${step.glowColor} 0%, transparent 70%)` }} />

      <motion.div
        className="relative rounded-2xl p-7 h-full overflow-hidden"
        style={{ background: `linear-gradient(145deg, ${step.bgFrom}, ${step.bgTo})`, border: '1px solid rgba(255,255,255,0.06)' }}
        whileHover={{ scale: 1.02, y: -5 }}
        transition={{ type: 'spring', stiffness: 400, damping: 28 }}
      >
        <div className="absolute -top-3 -right-1 font-bold select-none pointer-events-none"
          style={{ fontSize: 88, color: step.color, opacity: 0.055, lineHeight: 1 }}>{step.number}</div>

        <div className="relative w-16 h-16 mb-6">
          <motion.div className="absolute rounded-full border"
            style={{ width: 66, height: 66, top: '50%', left: '50%', transform: 'translate(-50%,-50%)', borderColor: `${step.color}28` }}
            animate={{ rotate: 360 }} transition={{ duration: 18, repeat: Infinity, ease: 'linear' }} />
          <motion.div className="relative w-16 h-16 rounded-2xl flex items-center justify-center z-10"
            style={{ background: `linear-gradient(135deg, ${step.color} 0%, ${step.color}cc 100%)`, boxShadow: `0 8px 32px ${step.color}50` }}
            animate={{ boxShadow: [`0 8px 32px ${step.color}40`, `0 12px 50px ${step.color}70`, `0 8px 32px ${step.color}40`] }}
            transition={{ duration: 3, repeat: Infinity }}>
            <IconComponent className="w-8 h-8 text-white" />
          </motion.div>
        </div>

        <div className="flex items-center gap-3 mb-3">
          <span className="text-xs font-mono uppercase tracking-[0.25em]" style={{ color: step.color }}>Step {step.number}</span>
          <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, ${step.color}55, transparent)` }} />
        </div>

        <h3 className="text-xl md:text-2xl font-medium text-[#EDEDED] mb-3 tracking-tight">{step.title}</h3>
        <p className="text-[#777] leading-relaxed text-sm mb-5">{step.description}</p>

        <div className="flex flex-wrap gap-2">
          {step.tags.map((tag) => (
            <span key={tag} className="text-xs font-mono px-3 py-1 rounded-full"
              style={{ background: `${step.color}15`, color: step.color, border: `1px solid ${step.color}28` }}>{tag}</span>
          ))}
        </div>

        <motion.div className="absolute bottom-0 left-0 h-0.5 rounded-full"
          style={{ background: `linear-gradient(90deg, ${step.color}, ${step.color}33)` }}
          initial={{ width: 0 }} whileInView={{ width: '100%' }} viewport={{ once: true }}
          transition={{ duration: 1.2, delay: 0.3 + index * 0.15, ease: 'easeOut' }} />
      </motion.div>
    </motion.div>
  );
};

/**
 * HowItWorks — 220vh sticky section.
 *
 * Scroll behaviour:
 *   0–50%   content is pinned, fully visible (dwell — gives 1 screen of pause)
 *   50–100% content translates UP and fades out as user scrolls away
 *
 * No useSpring on scroll — direct useTransform for zero lag.
 */
const HowItWorks = () => {
  const sectionRef = useRef(null);
  const isMobile = useIsMobile();
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start start', 'end end'] });

  // Content scrolls up and fades during the second half of the section
  const contentY = useTransform(scrollYProgress, [0.5, 1], ['0vh', '-30vh']);
  const contentOpacity = useTransform(scrollYProgress, [0.5, 0.85], [1, 0]);

  return (
    // 220vh = ~2 screens of dwell before content exits on Desktop. Natural flow on Mobile.
    <section ref={sectionRef} className="relative" style={{ height: isMobile ? 'auto' : '220vh' }} data-testid="how-it-works-section">
      <div className="relative py-24 h-auto md:sticky md:top-0 md:h-screen flex items-center justify-center md:overflow-hidden md:py-0">

        {/* Ambient glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(ellipse, rgba(99,102,241,0.055) 0%, transparent 70%)', filter: 'blur(50px)' }} />

        <motion.div
          className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12"
          style={{
            y: isMobile ? '0vh' : contentY,
            opacity: isMobile ? 1 : contentOpacity
          }}
        >
          {/* Header */}
          <motion.div className="text-center mb-14"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            <span className="inline-flex items-center gap-3 text-xs font-mono uppercase tracking-[0.3em] text-[#555] mb-7">
              <span className="w-10 h-px inline-block" style={{ background: 'linear-gradient(90deg, transparent, #555)' }} />
              How It Works
              <span className="w-10 h-px inline-block" style={{ background: 'linear-gradient(270deg, transparent, #555)' }} />
            </span>
            <h2 className="text-4xl md:text-6xl lg:text-7xl font-medium tracking-tighter text-[#EDEDED] mb-5">
              Three steps to{' '}
              <motion.span style={{ color: '#8B5CF6' }}
                animate={{ textShadow: ['0 0 20px rgba(139,92,246,0.3)', '0 0 50px rgba(139,92,246,0.7)', '0 0 20px rgba(139,92,246,0.3)'] }}
                transition={{ duration: 2.5, repeat: Infinity }}>
                mental clarity
              </motion.span>
            </h2>
            <p className="text-base md:text-lg text-[#777] max-w-xl mx-auto font-light leading-relaxed">
              Stop keeping score in your head.
            </p>
          </motion.div>

          {/* Three cards — normal vertical column on mobile, grid on desktop */}
          <div className="flex flex-col gap-6 md:grid md:grid-cols-3 lg:gap-8">
            {steps.map((step, index) => (
              <div key={step.number} className="w-full">
                <StepCard step={step} index={index} />
              </div>
            ))}
          </div>

          {/* Bottom pill */}
          <motion.div className="mt-12 text-center"
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.5 }}>
            <div className="inline-flex items-center gap-4 px-7 py-3.5 rounded-full"
              style={{ background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.14)' }}>
              <motion.div className="w-2 h-2 rounded-full bg-[#8B5CF6]"
                animate={{ scale: [1, 1.6, 1] }} transition={{ duration: 1.5, repeat: Infinity }} />
              <span className="text-sm font-mono text-[#555] leading-relaxed block md:inline">Every other tool asks you to organize first. Neuron asks for nothing except your thoughts.</span>
              <motion.div className="w-2 h-2 rounded-full bg-[#8B5CF6]"
                animate={{ scale: [1, 1.6, 1] }} transition={{ duration: 1.5, repeat: Infinity, delay: 0.5 }} />
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorks;
